import {
  useMutationAttachAuthorizedSetupIntent,
  useMutationAttachAuthorizedSetupIntentAsMember,
  useQueryPaymentMethodSetupIntent,
  useQueryPaymentMethodSetupIntentAsMember,
} from "@gymflow/api";
import { PaymentMethodSetupIntent } from "@gymflow/types";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useContext, useState } from "react";

import { ModalContext, ModalWrapper, useClubSettings } from "../../providers";
import { ToastContext } from "../../providers/ToastProvider/context";
import useGymflowModels from "../../store";
import { Button, ErrorMessage, Spinner } from "../atoms";
import { VerifyMicroDepositModal } from "./VerifyMicroDepositModal";

export type AddPaymentMethodModalProps = {
  userMemberId: string;
  onConfirm?: (newPaymentMethod?: string) => Promise<void>;
  asMember?: boolean;
};

export const AddPaymentMethodModal = (props: AddPaymentMethodModalProps) => {
  const { club, properties, webSettings } = useClubSettings();
  const { api } = useGymflowModels();
  const { popModal } = useContext(ModalContext);
  const pmSetupIntentQueryAsStaff = useQueryPaymentMethodSetupIntent({
    api,
    memberId: props?.userMemberId,
    enabled: !props.asMember,
  });
  const pmSetupIntentQueryAsMember = useQueryPaymentMethodSetupIntentAsMember({
    api,
    memberId: props?.userMemberId,
    enabled: !!props.asMember,
  });
  const { data: paymentMethodSetupIntent } = !props.asMember
    ? pmSetupIntentQueryAsStaff
    : pmSetupIntentQueryAsMember;
  const isDarkMode = document.querySelector("html")!.classList.contains("dark");

  return (
    <ModalWrapper onCancel={popModal}>
      {!club || !paymentMethodSetupIntent ? (
        <Spinner />
      ) : (
        <Elements
          stripe={loadStripe(properties.stripePublicKey, {
            stripeAccount: club.stripeApplicationId,
          })}
          options={{
            clientSecret: paymentMethodSetupIntent.setupIntentClientSecret,
            loader: "auto",
            appearance: {
              variables: {
                colorPrimary: webSettings?.primaryColor ?? "#8746ec",
              },
              theme: isDarkMode ? "night" : "stripe",
            },
          }}
        >
          <AddPaymentMethodModalNewContents
            {...props}
            paymentMethodSetupIntent={paymentMethodSetupIntent}
          />
        </Elements>
      )}
    </ModalWrapper>
  );
};

const AddPaymentMethodModalNewContents = ({
  userMemberId,
  onConfirm,
  asMember,
  paymentMethodSetupIntent,
}: AddPaymentMethodModalProps & {
  paymentMethodSetupIntent: PaymentMethodSetupIntent;
}) => {
  const { popModal, stackModal } = useContext(ModalContext);
  const stripe = useStripe();
  const elements = useElements();
  const { api } = useGymflowModels();
  const attachAuthorizedSetupIntentMutationAsStaff =
    useMutationAttachAuthorizedSetupIntent({ api });
  const attachAuthorizedSetupIntentMutationAsMember =
    useMutationAttachAuthorizedSetupIntentAsMember({ api });
  const attachAuthorizedPaymentMethodIntentMutation = asMember
    ? attachAuthorizedSetupIntentMutationAsMember
    : attachAuthorizedSetupIntentMutationAsStaff;

  const { toast } = useContext(ToastContext);
  const [stripeError, setStripeError] = useState<string>();
  const onSubmit = async () => {
    if (!stripe || !elements) return;
    await elements.submit();
    const paymentMethodResult = await stripe.confirmSetup({
      elements,
      clientSecret: paymentMethodSetupIntent.setupIntentClientSecret,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (paymentMethodResult.error) {
      setStripeError(paymentMethodResult.error.message);
      return;
    }
    const paymentMethod =
      await attachAuthorizedPaymentMethodIntentMutation.mutateAsync({
        memberId: userMemberId,
        setupIntentId: paymentMethodResult.setupIntent.id,
      });
    if (paymentMethod.status === "PENDING" && !asMember) {
      popModal();
      stackModal(
        <VerifyMicroDepositModal
          as="staff"
          userMemberId={userMemberId}
          paymentMethodId={paymentMethod.paymentMethodId}
        />,
      );
      return;
    } else {
      const paymentMethodContainer =
        paymentMethodResult.setupIntent.payment_method;

      if (!paymentMethodContainer) return;
      const stripePaymentMethod =
        typeof paymentMethodContainer === "string"
          ? paymentMethodContainer
          : paymentMethodContainer.id;
      await onConfirm?.(stripePaymentMethod);

      toast({ message: "Payment method added." });
    }
  };
  const [completed, setCompleted] = useState(false);
  return (
    <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden">
      <div className="dark:text-gray-0 text-lg font-semibold text-gray-950">
        Add Payment Method
      </div>
      <div className="flex flex-col gap-1">
        <PaymentElement
          onChange={(paymentMethod) => {
            setStripeError(undefined);
            setCompleted(paymentMethod.complete);
          }}
          options={{
            wallets: {
              applePay: asMember ? "auto" : "never",
              googlePay: asMember ? "auto" : "never",
            },
          }}
        />
        {stripeError && <ErrorMessage text={stripeError} />}
      </div>
      <div className="flex gap-4">
        <Button
          onClick={() => {
            popModal();
          }}
          className="flex-1"
        >
          Cancel
        </Button>

        <Button
          intent="secondary"
          className="mt-0 flex-1"
          disabled={!!stripeError || !completed}
          onClick={async () => {
            await onSubmit();
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
